import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { LegalLayout } from '../components/LegalLayout';

const CONTENT = {
  fr: {
    title: "Politique de confidentialité",
    updated: "Dernière mise à jour : janvier 2026",
    sections: [
      {
        title: "1. Données collectées",
        body: "Nous traitons : (a) les données de compte praticien (nom, email, mot de passe haché) ; (b) les images médicales téléversées et leurs métadonnées techniques (dont métadonnées DICOM le cas échéant) ; (c) le contexte clinique saisi (âge, sexe, symptômes, traumatisme) ; (d) les analyses générées, annotations, questions de suivi et pièces jointes ; (e) des journaux techniques nécessaires au fonctionnement et à la sécurité du service.",
      },
      {
        title: "2. Finalités",
        body: "Ces données sont traitées pour : fournir l'analyse assistée par IA et ses fonctionnalités associées (rapports, PDF, lecture vocale, historique, statistiques), assurer la traçabilité professionnelle des analyses, garantir la sécurité du service et répondre à vos demandes de support.",
      },
      {
        title: "3. Traitement par intelligence artificielle",
        body: "Pour produire les analyses et la synthèse vocale, les images et le contexte clinique sont transmis de manière sécurisée à notre fournisseur de modèles d'IA. Nous vous recommandons de pseudonymiser les données patient avant téléversement lorsque cela est possible.",
      },
      {
        title: "4. Hébergement et sécurité",
        body: "Les données sont stockées dans notre base sécurisée. Les échanges sont chiffrés en transit (HTTPS). L'accès aux analyses nécessite une authentification ; les liens de partage en lecture seule ne sont accessibles qu'aux personnes disposant du lien que vous générez.",
      },
      {
        title: "5. Conservation et suppression",
        body: "Les analyses sont conservées tant que votre compte est actif. Vous pouvez supprimer une analyse à tout moment depuis l'historique : cette suppression est définitive. Vous pouvez demander la suppression de votre compte et de l'ensemble des données associées en nous contactant.",
      },
      {
        title: "6. Partage des données",
        body: "Nous ne vendons pas vos données. Elles ne sont partagées qu'avec : (a) notre fournisseur d'IA, strictement pour produire les analyses demandées ; (b) les destinataires des liens de partage que vous créez volontairement ; (c) les autorités lorsque la loi l'exige.",
      },
      {
        title: "7. Vos droits",
        body: "Conformément à la réglementation applicable (dont le RGPD), vous disposez de droits d'accès, de rectification, d'effacement, de limitation et d'opposition sur vos données. Pour les exercer, écrivez-nous à support@openmeta.fr. Vous pouvez également introduire une réclamation auprès de l'autorité de contrôle compétente.",
      },
      {
        title: "8. Contact",
        body: "Pour toute question relative à la protection des données : support@openmeta.fr.",
      },
    ],
  },
  en: {
    title: "Privacy Policy",
    updated: "Last updated: January 2026",
    sections: [
      {
        title: "1. Data we collect",
        body: "We process: (a) practitioner account data (name, email, hashed password); (b) uploaded medical images and their technical metadata (including DICOM metadata where applicable); (c) the clinical context you enter (age, sex, symptoms, trauma); (d) generated analyses, annotations, follow-up questions and attachments; (e) technical logs required for the operation and security of the service.",
      },
      {
        title: "2. Purposes",
        body: "This data is processed to: provide AI-assisted analysis and its related features (reports, PDFs, voice playback, history, statistics), ensure professional traceability of analyses, secure the service, and answer your support requests.",
      },
      {
        title: "3. AI processing",
        body: "To produce analyses and voice synthesis, images and clinical context are securely transmitted to our AI model provider. We recommend pseudonymizing patient data before upload whenever possible.",
      },
      {
        title: "4. Hosting and security",
        body: "Data is stored in our secured database. Exchanges are encrypted in transit (HTTPS). Access to analyses requires authentication; read-only share links are accessible only to people holding the link you generate.",
      },
      {
        title: "5. Retention and deletion",
        body: "Analyses are kept for as long as your account is active. You may delete an analysis at any time from the history: deletion is permanent. You may request deletion of your account and all associated data by contacting us.",
      },
      {
        title: "6. Data sharing",
        body: "We do not sell your data. It is shared only with: (a) our AI provider, strictly to produce the requested analyses; (b) recipients of the share links you voluntarily create; (c) authorities where required by law.",
      },
      {
        title: "7. Your rights",
        body: "In accordance with applicable regulations (including GDPR), you have rights of access, rectification, erasure, restriction and objection regarding your data. To exercise them, write to support@openmeta.fr. You may also lodge a complaint with the competent supervisory authority.",
      },
      {
        title: "8. Contact",
        body: "For any data protection question: support@openmeta.fr.",
      },
    ],
  },
};

export default function PrivacyPage() {
  const { i18n } = useTranslation();
  const c = CONTENT[i18n.language === 'en' ? 'en' : 'fr'];

  return (
    <LegalLayout>
      <div data-testid="privacy-page">
        <div className="flex items-center gap-3 mb-1">
          <ShieldCheck size={26} className="text-cyan-400" />
          <h1 className="text-2xl font-bold text-foreground">{c.title}</h1>
        </div>
        <p className="text-xs text-muted-foreground mb-8">{c.updated}</p>

        <Card className="bg-[hsl(210,18%,9%)] border-border">
          <CardContent className="py-6 space-y-6">
            {c.sections.map((s, i) => (
              <section key={i} data-testid={`privacy-section-${i}`}>
                <h2 className="text-sm font-semibold text-foreground mb-1.5">{s.title}</h2>
                <p className="text-sm text-foreground/75 leading-relaxed">{s.body}</p>
              </section>
            ))}
          </CardContent>
        </Card>
      </div>
    </LegalLayout>
  );
}
